import { ref, computed } from "vue";
import { defineStore } from "pinia";
import moment from "moment";
import useMyFetch from "@stores/useMyFetch";
import { useToastsStore } from "@stores/toastsStore";
import { deepCopy, downloadBlob } from "@utils/toolBox";
import { getClientId } from "@utils/clientId";
import { useDirectUpload } from "@hooks/useDirectUpload";
import { useDebouncedSearchQuery } from "@utils/composables/filters/useDebouncedSearchQuery";
import type { JcdBenchmark, JcdCampaign, JcdCampaignPayload, JcdReferentials } from "@/types";
import { captureEvent } from "@/lib/analytics";

export const PAGE_SIZE = 25;

export const useJcdCampaignsStore = defineStore("jcdCampaigns", () => {
  const campaigns = ref<JcdCampaign[]>([]);
  const campaignsCount = ref(0);
  const currentPage = ref(1);
  const searchQuery = ref("");
  const yearFilter = ref<string[]>([]);
  const weekFilter = ref<string[]>([]);
  const familyFilter = ref<string[]>([]);
  const familyClassFilter = ref<string[]>([]);
  const supportFilter = ref<string[]>([]);
  const networksFilter = ref<string[]>([]);
  const formatsFilter = ref<string[]>([]);
  const levelFilter = ref<string[]>([]);
  const currentCampaign = ref<Partial<JcdCampaign>>({});
  const initialCampaign = ref<Partial<JcdCampaign>>({});
  const isNewCampaign = ref(false);
  const referentials = ref<JcdReferentials | null>(null);
  const benchmarks = ref<JcdBenchmark[] | null>(null);
  const isLoading = ref(false);

  const toasts = useToastsStore();
  const serverErrorDetail = (error: unknown) => [error ?? []].flat().join(", ") || null;

  const editableFields = [
    "brand",
    "logo_url",
    "year",
    "week",
    "starts_on",
    "ends_on",
    "family",
    "family_class",
    "support",
    "level",
    "gross_budget",
    "faces_count",
    "visuals_count",
    "networks",
    "formats",
    "mosaic_code",
    "masked_visual_urls",
    "unmasked_visual_urls",
    "attribution_enabled",
    "competitor_brands",
    "intents",
    "agglomeration_mode",
    "jcd_agglomeration_ids",
    "jcd_benchmark_id",
    "reco_benchmark",
    "attribution_benchmark",
    "intention_benchmark",
  ] as const;

  const hasCampaignChanged = computed(() =>
    editableFields.some(
      (field) => JSON.stringify(currentCampaign.value[field]) !== JSON.stringify(initialCampaign.value[field]),
    ),
  );

  const asPercent = (value: number | null | undefined) => (value == null ? null : Math.round(value * 100));
  const asFraction = (value: number | null | undefined) => (value == null ? null : value / 100);

  const toEntity = (campaign: JcdCampaign): JcdCampaign => ({
    ...campaign,
    reco_benchmark: asPercent(campaign.reco_benchmark),
    attribution_benchmark: asPercent(campaign.attribution_benchmark),
    intention_benchmark: asPercent(campaign.intention_benchmark),
  });

  const toPayload = (): JcdCampaignPayload => {
    const campaign = currentCampaign.value;
    return {
      brand: campaign.brand,
      logo_url: campaign.logo_url || null,
      year: campaign.year,
      week: campaign.week,
      starts_on: moment(campaign.starts_on).format("YYYY-MM-DD"),
      ends_on: moment(campaign.ends_on).format("YYYY-MM-DD"),
      family: campaign.family || null,
      family_class: campaign.family_class || null,
      support: campaign.support || null,
      level: campaign.level || null,
      gross_budget: campaign.gross_budget ?? null,
      faces_count: campaign.faces_count ?? null,
      visuals_count: campaign.visuals_count ?? null,
      networks: campaign.networks || [],
      formats: campaign.formats || [],
      mosaic_code: campaign.mosaic_code || null,
      masked_visual_urls: campaign.masked_visual_urls || [],
      unmasked_visual_urls: campaign.unmasked_visual_urls || [],
      attribution_enabled: campaign.attribution_enabled,
      competitor_brands: (campaign.competitor_brands || []).filter((brand) => brand.trim() !== ""),
      intents: (campaign.intents || []).filter((intent) => intent.trim() !== ""),
      agglomeration_mode: campaign.agglomeration_mode,
      jcd_agglomeration_ids: campaign.jcd_agglomeration_ids || [],
      jcd_benchmark_id: campaign.jcd_benchmark_id ?? null,
      reco_benchmark: asFraction(campaign.reco_benchmark),
      attribution_benchmark: asFraction(campaign.attribution_benchmark),
      intention_benchmark: asFraction(campaign.intention_benchmark),
    };
  };

  const filterRefs = {
    year: yearFilter,
    week: weekFilter,
    family: familyFilter,
    family_class: familyClassFilter,
    support: supportFilter,
    networks: networksFilter,
    formats: formatsFilter,
    level: levelFilter,
  };

  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (searchQuery.value.trim()) params.append("search", searchQuery.value.trim());
    Object.entries(filterRefs).forEach(([key, filterRef]) => {
      filterRef.value.forEach((value) => params.append(`${key}[]`, value));
    });
    return params;
  };

  const fetch = async (page: number | null = null) => {
    const params = buildFilterParams();
    params.append("page", String(page || currentPage.value));
    params.append("per_page", String(PAGE_SIZE));

    isLoading.value = true;
    const { data } = await useMyFetch(`/jcd/campaigns?${params}`).json<{
      campaigns: JcdCampaign[];
      campaigns_count: number;
    }>();
    isLoading.value = false;
    if (data.value) {
      campaigns.value = data.value.campaigns;
      campaignsCount.value = data.value.campaigns_count;
    }
    if (page) currentPage.value = page;
    return campaigns.value;
  };

  const fetchOne = async (id: number) => {
    const { data, error } = await useMyFetch(`/jcd/campaigns/${id}`).json<JcdCampaign>();
    if (error.value || !data.value) throw error.value;
    currentCampaign.value = toEntity(data.value);
    initialCampaign.value = deepCopy(currentCampaign.value);
    isNewCampaign.value = false;
    return currentCampaign.value;
  };

  const initCampaign = () => {
    currentCampaign.value = {
      brand: "",
      logo_url: null,
      family: null,
      family_class: null,
      support: null,
      level: null,
      gross_budget: null,
      faces_count: null,
      visuals_count: null,
      networks: [],
      formats: [],
      mosaic_code: null,
      masked_visual_urls: [],
      unmasked_visual_urls: [],
      attribution_enabled: true,
      competitor_brands: [],
      intents: [],
      agglomeration_mode: "all",
      jcd_agglomeration_ids: [],
      jcd_benchmark_id: null,
      reco_benchmark: null,
      attribution_benchmark: null,
      intention_benchmark: null,
    };
    initialCampaign.value = deepCopy(currentCampaign.value);
    isNewCampaign.value = true;
  };

  const fetchReferentials = async () => {
    if (referentials.value) return referentials.value;
    const { data, error } = await useMyFetch("/jcd/campaigns/referentials").json<JcdReferentials>();
    if (error.value) throw error.value;
    referentials.value = data.value;
    return data.value;
  };

  const fetchBenchmarks = async () => {
    if (benchmarks.value) return benchmarks.value;
    const { data, error } = await useMyFetch("/jcd/benchmarks").json<JcdBenchmark[]>();
    if (error.value) throw error.value;
    benchmarks.value = data.value;
    return data.value;
  };

  const create = async () => {
    const { response, data, error } = await useMyFetch("/jcd/campaigns")
      .post({ jcd_campaign: toPayload() })
      .json<JcdCampaign>();
    if (response.value?.ok && data.value) {
      currentCampaign.value = toEntity(data.value);
      initialCampaign.value = deepCopy(currentCampaign.value);
      isNewCampaign.value = false;

      captureEvent("jcd_campaign_created", { jcd_campaign_id: data.value.id });
    } else {
      toasts.error("Impossible de créer la campagne", serverErrorDetail(error.value));
    }
    return Boolean(response.value?.ok);
  };

  const update = async () => {
    const { response, data, error } = await useMyFetch(`/jcd/campaigns/${currentCampaign.value.id}`)
      .patch({ jcd_campaign: toPayload() })
      .json<JcdCampaign>();
    if (response.value?.ok && data.value) {
      currentCampaign.value = toEntity(data.value);
      initialCampaign.value = deepCopy(currentCampaign.value);

      captureEvent("jcd_campaign_updated", { jcd_campaign_id: data.value.id });
    } else {
      toasts.error("Impossible d'enregistrer la campagne", serverErrorDetail(error.value));
    }
    return Boolean(response.value?.ok);
  };

  const exportCampaigns = async () => {
    const { response, data } = await useMyFetch(`/jcd/campaigns/export?${buildFilterParams()}`).get().blob();
    if (response.value?.ok && data.value) downloadBlob(data.value, response.value, "campagnes-jcdecaux.csv");
    else toasts.error("L'export a échoué");
  };

  const uploadToActiveStorage = (file: File): Promise<{ signed_id: string }> =>
    new Promise((resolve, reject) => {
      const { uploadFile } = useDirectUpload(
        (blob: { signed_id: string }) => resolve(blob),
        (error: unknown) => reject(error),
      );
      uploadFile(file);
    });

  const importCampaigns = async (file: File) => {
    try {
      const blob = await uploadToActiveStorage(file);
      const { response } = await useMyFetch("/jcd/campaigns/import")
        .post({ signed_blob_id: blob.signed_id, client_id: getClientId() })
        .json();
      if (response.value?.ok) captureEvent("jcd_campaigns_imported");
      else toasts.error("Impossible de lancer l'import");

      return Boolean(response.value?.ok);
    } catch {
      toasts.error("L'upload du fichier a échoué");
      return false;
    }
  };

  const refreshCampaign = async (id: number) => {
    const { data } = await useMyFetch(`/jcd/campaigns/${id}`).json<JcdCampaign>();
    if (!data.value) return null;
    const index = campaigns.value.findIndex((campaign) => campaign.id === id);
    if (index !== -1) campaigns.value[index] = data.value;
    return data.value;
  };

  const generatePpt = async (id: number) => {
    const { response } = await useMyFetch(`/jcd/campaigns/${id}/generate_ppt`)
      .post({ client_id: getClientId() })
      .json();
    if (response.value?.ok) captureEvent("jcd_ppt_generated", { jcd_campaign_id: id });
    else toasts.error("Impossible de lancer la génération du rapport");
    return Boolean(response.value?.ok);
  };

  const generateSurvey = async (year: number, week: number) => {
    const { response } = await useMyFetch("/jcd/campaigns/generate_survey")
      .post({ year, week, client_id: getClientId() })
      .json();
    if (response.value?.ok) captureEvent("jcd_survey_generated", { year, week });
    else toasts.error("Impossible de lancer la génération du questionnaire");
    return Boolean(response.value?.ok);
  };

  const destroy = async (id: number) => {
    const { error } = await useMyFetch(`/jcd/campaigns/${id}`).delete();
    if (error.value) throw error.value;
    captureEvent("jcd_campaign_deleted", { jcd_campaign_id: id });

    campaigns.value = campaigns.value.filter((campaign) => campaign.id !== id);
    campaignsCount.value -= 1;
  };

  const clearFilters = async () => {
    Object.values(filterRefs).forEach((filterRef) => {
      filterRef.value = [];
    });
    await fetch(1);
  };

  const resetAllFilters = () => {
    resetSearchQuery();
    Object.values(filterRefs).forEach((filterRef) => {
      filterRef.value = [];
    });
    currentPage.value = 1;
    campaigns.value = [];
    campaignsCount.value = 0;
  };

  const { resetSearchQuery, setSearchQuery } = useDebouncedSearchQuery(searchQuery, () => fetch(1));

  return {
    campaigns,
    campaignsCount,
    currentPage,
    searchQuery,
    yearFilter,
    weekFilter,
    familyFilter,
    familyClassFilter,
    supportFilter,
    networksFilter,
    formatsFilter,
    levelFilter,
    currentCampaign,
    initialCampaign,
    isNewCampaign,
    hasCampaignChanged,
    referentials,
    benchmarks,
    isLoading,
    fetch,
    fetchOne,
    refreshCampaign,
    initCampaign,
    fetchReferentials,
    fetchBenchmarks,
    create,
    update,
    destroy,
    exportCampaigns,
    importCampaigns,
    generateSurvey,
    generatePpt,
    clearFilters,
    resetAllFilters,
    setSearchQuery,
  };
});
